import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
  FlatList,
  Linking,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import moment from 'moment';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const MyInvoices = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('Latest');

  // Collapse sections state
  const [packagesExpanded, setPackagesExpanded] = useState(true);
  const [productsExpanded, setProductsExpanded] = useState(true);

  // Data states
  const [packagesData, setPackagesData] = useState([]);
  const [productsData, setProductsData] = useState([]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = () => {
    ajax
      .get(Urls.GET_Invoice_DOCUMENTS)
      .then(({ data }) => {
        if (data?.status && data?.payload) {
          if (data?.payload?.purchasedPackages) {
            const list = data.payload.purchasedPackages.map((e) => ({
              id: e?.id || Math.random().toString(),
              name: e?.package?.name || e?.package?.type || 'Package Invoice',
              cost: e?.price ? `£${Number(e.price).toFixed(2)}` : '£0.00',
              rawCost: Number(e?.price || 0),
              date: e?.purchaseDate ? moment(e.purchaseDate).format('DD/MM/YYYY') : '-',
              rawDate: e?.purchaseDate ? moment(e.purchaseDate).valueOf() : 0,
              url: e?.invoiceUrl || e?.document_url || '',
            }));
            setPackagesData(list);
          }
          if (data?.payload?.purchasedProducts) {
            const list = data.payload.purchasedProducts.map((e) => ({
              id: e?.id || Math.random().toString(),
              name: e?.product?.name || 'Product Invoice',
              cost: e?.price ? `£${Number(e.price).toFixed(2)}` : '£0.00',
              rawCost: Number(e?.price || 0),
              date: e?.purchaseDate ? moment(e.purchaseDate).format('DD/MM/YYYY') : '-',
              rawDate: e?.purchaseDate ? moment(e.purchaseDate).valueOf() : 0,
              url: e?.invoiceUrl || e?.document_url || '',
            }));
            setProductsData(list);
          }
        }
      })
      .catch((err) => {
        console.log('error fetch invoices', err);
      });
  };

  const filterAndSort = (arr) => {
    let result = [...arr];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => item.name.toLowerCase().includes(q) || item.date.includes(q));
    }
    result.sort((a, b) => {
      if (sortOrder === 'Latest') return b.rawDate - a.rawDate;
      if (sortOrder === 'Oldest') return a.rawDate - b.rawDate;
      if (sortOrder === 'A-Z') return a.name.localeCompare(b.name);
      if (sortOrder === 'Z-A') return b.name.localeCompare(a.name);
      return 0;
    });
    return result;
  };

  const filteredPackages = filterAndSort(packagesData);
  const filteredProducts = filterAndSort(productsData);

  const handleExportAll = () => {
    const allUrls = [...packagesData, ...productsData].map((i) => i.url).filter(Boolean);
    if (allUrls.length > 0) {
      Linking.openURL(allUrls[0]);
    }
  };

  const renderSectionHeader = (title, itemCount, isExpanded, onToggle) => (
    <TouchableOpacity
      style={styles.sectionHeader}
      onPress={onToggle}
      activeOpacity={0.8}
    >
      <View style={styles.sectionHeaderLeft}>
        <View style={styles.sectionIconBox}>
          <Feather name="bookmark" size={16} color="#6C2BD9" />
        </View>
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.countBadge}>
          <Text style={styles.countBadgeText}>{itemCount} Items</Text>
        </View>
      </View>
      <Feather name={isExpanded ? 'chevron-up' : 'chevron-down'} size={18} color="#6B7280" />
    </TouchableOpacity>
  );

  const renderInvoiceRow = (item) => {
    return (
      <View key={item.id} style={[styles.invoiceRow, isMobile && styles.invoiceCardMobile]}>
        <View style={styles.rowTitleGroup}>
          <View style={styles.itemIconCircle}>
            <Feather name="home" size={14} color="#6C2BD9" />
          </View>
          <Text style={styles.itemTitleText}>{item.name}</Text>
        </View>

        <Text style={[styles.costText, isMobile && styles.mobileCellText]}>
          {isMobile && <Text style={styles.mobileLabel}>Cost: </Text>}
          {item.cost}
        </Text>

        <Text style={[styles.dateText, isMobile && styles.mobileCellText]}>
          {isMobile && <Text style={styles.mobileLabel}>Date: </Text>}
          {item.date}
        </Text>

        <View style={[styles.actionCol, isMobile && styles.mobileActionCol]}>
          <TouchableOpacity
            style={styles.viewBtn}
            onPress={() => {
              if (item.url) Linking.openURL(item.url);
            }}
            activeOpacity={0.7}
          >
            <Feather name="eye" size={16} color="#6C2BD9" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb Header */}
      <Text style={styles.breadcrumbText}>Payments › My Invoices</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>My Invoices</Text>
      </View>

      {/* Top Controls Row matching Figma */}
      <View style={[styles.controlsRow, isMobile && styles.controlsRowMobile]}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search invoice name..."
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        <View style={styles.rightControlsGroup}>
          <TouchableOpacity
            style={styles.sortBtn}
            onPress={() => {
              const options = ['Latest', 'Oldest', 'A-Z', 'Z-A'];
              const nextIndex = (options.indexOf(sortOrder) + 1) % options.length;
              setSortOrder(options[nextIndex]);
            }}
            activeOpacity={0.8}
          >
            <Text style={styles.sortBtnText}>Sort by: {sortOrder}</Text>
            <Feather name="chevron-down" size={14} color="#374151" style={{ marginLeft: 4 }} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.exportBtn} onPress={handleExportAll} activeOpacity={0.8}>
            <Feather name="download" size={14} color="#374151" style={{ marginRight: 6 }} />
            <Text style={styles.exportBtnText}>Export All</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* SECTION 1: PROTREASURE INVOICES (PURCHASED PACKAGES) */}
      <View style={styles.sectionContainer}>
        {renderSectionHeader(
          'ProTreasure Invoices',
          filteredPackages.length,
          packagesExpanded,
          () => setPackagesExpanded((prev) => !prev)
        )}

        {packagesExpanded && (
          <View style={styles.sectionBody}>
            {!isMobile && (
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.headerCell, { flex: 2 }]}>INVOICE / ITEM NAME</Text>
                <Text style={styles.headerCell}>COST</Text>
                <Text style={styles.headerCell}>PURCHASE DATE</Text>
                <Text style={[styles.headerCell, { textAlign: 'right', flex: 0.8 }]}>ACTION</Text>
              </View>
            )}

            {filteredPackages.length ? (
              filteredPackages.map(renderInvoiceRow)
            ) : (
              <View style={styles.emptyBox}>
                <Text style={styles.emptyText}>No ProTreasure invoices available.</Text>
              </View>
            )}
          </View>
        )}
      </View>

      {/* SECTION 2: PRODUCT INVOICES (PURCHASED PRODUCTS) */}
      <View style={styles.sectionContainer}>
        {renderSectionHeader(
          'Product Invoices',
          filteredProducts.length,
          productsExpanded,
          () => setProductsExpanded((prev) => !prev)
        )}

        {productsExpanded && (
          <View style={styles.sectionBody}>
            {!isMobile && (
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.headerCell, { flex: 2 }]}>INVOICE / ITEM NAME</Text>
                <Text style={styles.headerCell}>COST</Text>
                <Text style={styles.headerCell}>PURCHASE DATE</Text>
                <Text style={[styles.headerCell, { textAlign: 'right', flex: 0.8 }]}>ACTION</Text>
              </View>
            )}

            {filteredProducts.length ? (
              filteredProducts.map(renderInvoiceRow)
            ) : (
              <View style={styles.emptyBox}>
                <Text style={styles.emptyText}>No product invoices available.</Text>
              </View>
            )}
          </View>
        )}
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: MyInvoices,
  showBackButton: true,
  backNavigation: 'Payments',
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  /* CONTROLS ROW */
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  controlsRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    height: 40,
    ...SHADOWS.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  rightControlsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sortBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 40,
    ...SHADOWS.sm,
  },
  sortBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },

  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 16,
    height: 40,
    ...SHADOWS.sm,
  },
  exportBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#374151',
  },

  /* SECTION CONTAINER */
  sectionContainer: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 20,
    overflow: 'hidden',
    ...SHADOWS.sm,
  },

  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionIconBox: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
    marginRight: 10,
  },
  countBadge: {
    backgroundColor: '#F5F3FF',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  countBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6C2BD9',
  },

  sectionBody: {
    padding: 16,
  },

  /* TABLE WEB */
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  headerCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 0.5,
  },

  invoiceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
  },
  rowTitleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 2,
  },
  itemIconCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  itemTitleText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },

  costText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  dateText: {
    flex: 1,
    fontSize: 13,
    color: '#4B5563',
  },
  actionCol: {
    flex: 0.8,
    alignItems: 'flex-end',
  },
  viewBtn: {
    padding: 4,
  },

  /* MOBILE CARD */
  invoiceCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    marginBottom: 10,
  },
  mobileCellText: {
    fontSize: 12,
    marginVertical: 2,
  },
  mobileLabel: {
    fontWeight: '600',
    color: '#6B7280',
  },
  mobileActionCol: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
    paddingTop: 8,
    marginTop: 8,
  },

  emptyBox: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#6B7280',
  },
});
